/**
 * Runtime config the SPA reads at boot. Populated by `/spaces-config.js` (served
 * by the client Worker from per-env wrangler vars) into `window.__SPACES_CONFIG__`.
 * Hostnames are CONFIG, never hardcoded here — this module only reads + validates.
 */

import type { Environment } from '@spaces/contracts';

/** The shape `/spaces-config.js` injects. */
export interface SpacesClientConfig {
    env: Environment;
    /** pairing Worker base URL (POST /pair, /pair/code, /token/refresh). */
    pairingUrl: string;
    /** room-do Worker base URL (WS GET /rooms/:id/connect). */
    roomDoUrl: string;
    /** Jitsi meeting domain the TV iframe joins (e.g. alpha.jitsi.net in dev). */
    meetingDomain: string;
    /**
     * 8x8 meeting TENANT NAME the TV joins under, as the room-address path prefix:
     * the iframe joins `<meetingDomain>/<meetingTenant>/<room>`. Non-empty (e.g.
     * "emchodemo") routes the meeting into the customer's own environment so
     * server-side recording persists there; EMPTY means a flat/anonymous join (no
     * prefix — the alpha dev loop). Distinct from the JWT `cid` (the SF customer id).
     */
    meetingTenant: string;
    /** URL of the Jitsi external_api.js to load at runtime. */
    externalApiSrc: string;
    /**
     * Whether to pass the self-signed meetingJwt to the iframe. FALSE in dev: the
     * dev-key-signed jwt won't validate on anonymous alpha, so the TV joins without
     * it. TRUE once a real-key signer + matching meeting domain are wired (cutover).
     */
    useMeetingJwt: boolean;
    /**
     * Host shown for remote pickup / laptop share (e.g. `cast.8x8.vc` in prod). The
     * Remote `/` entry + `/share` page are served by the client worker, so this host
     * resolves to the client. Config per env (never the hardcoded legacy share.8x8.vc).
     */
    shareHost: string;
}

declare global {
    interface Window {
        __SPACES_CONFIG__?: Partial<SpacesClientConfig>;
        __SPACES_CONFIG_ERROR__?: string;
    }
}

let cached: SpacesClientConfig | null = null;

function req(cfg: Partial<SpacesClientConfig>, key: keyof SpacesClientConfig): string {
    const value = cfg[key];

    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(`Spaces client config is missing '${String(key)}' — check /spaces-config.js`);
    }

    return value;
}

/**
 * Resolve the runtime config, failing loudly if `/spaces-config.js` did not load
 * or is incomplete (house rule: never silently default config).
 */
export function getConfig(): SpacesClientConfig {
    if (cached) {
        return cached;
    }

    if (window.__SPACES_CONFIG_ERROR__) {
        throw new Error(`Spaces client misconfigured: ${window.__SPACES_CONFIG_ERROR__}`);
    }

    const raw = window.__SPACES_CONFIG__;

    if (!raw) {
        throw new Error('Spaces client config absent — /spaces-config.js did not load');
    }

    const env = raw.env;

    if (env !== 'dev' && env !== 'staging' && env !== 'prod') {
        throw new Error(`Spaces client config has an invalid env: ${JSON.stringify(env)}`);
    }

    // meetingTenant is declared per-env but may be EMPTY (anonymous join); validate
    // its presence/type without rejecting '' (req() would reject the empty string).
    if (typeof raw.meetingTenant !== 'string') {
        throw new Error("Spaces client config is missing 'meetingTenant' — check /spaces-config.js");
    }

    cached = {
        env,
        pairingUrl: req(raw, 'pairingUrl').replace(/\/+$/, ''),
        roomDoUrl: req(raw, 'roomDoUrl').replace(/\/+$/, ''),
        meetingDomain: req(raw, 'meetingDomain'),
        meetingTenant: raw.meetingTenant.replace(/^\/+|\/+$/g, ''),
        externalApiSrc: req(raw, 'externalApiSrc'),
        useMeetingJwt: raw.useMeetingJwt === true,
        shareHost: req(raw, 'shareHost')
    };

    return cached;
}
